
import { ReactElement, useEffect, useState, useImperativeHandle, forwardRef, useRef } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { EllipsisVertical, Loader2, Pencil, Trash2, Plus } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/shared/hooks/useIsMobile'
import { type Product, addProductVariants, type CreateVariantPayload } from '@/lib/productService'
import { updateVariant, deleteVariant } from '@/lib/variantService'
import {
    ImageUrlPopover,
    CostInput,
    costStringSchema,
    parseCostToNumber,
    formatCostFromNumber,
    DatePicker,
} from '@/shared/components/ui'
import { Label } from '@/components/ui/label'
import * as Editable from '@/components/ui/editable'
import { NEEDS_REVIEW_QUERY_KEY, NEEDS_REVIEW_COUNT_QUERY_KEY } from '@/features/sku-alias/api/needsReview'

/** Handle exposed by ProductVariantsManager for parent coordination */
export interface ProductVariantsManagerHandle {
    /** Whether the variant form has unsaved changes */
    isDirty: boolean
    /** Whether a save/update operation is in progress */
    isSaving: boolean
    /** Submit the current variant form */
    submit: () => Promise<void>
    /** Reset the form to its original values */
    reset: () => void
}

const variantFormSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    sku: z.string().min(1, 'SKU is required'),
    originalCost: costStringSchema,
    originalCurrency: z.string().optional(),
    costDate: z.string().optional().nullable(),
    imageUrl: z.string().optional().nullable(),
})

type VariantFormValues = z.infer<typeof variantFormSchema>

export type ViewMode = 'edit' | 'create'

interface ProductVariantsManagerProps {
    product: Product
    /** Callback fired whenever the form dirty state changes */
    onDirtyChange?: (isDirty: boolean) => void
    /** Callback fired whenever a save/update operation is in progress */
    onSavingChange?: (isSaving: boolean) => void
    /** Callback fired whenever the selected variant changes */
    onSelectedVariantChange?: (variantId: number | null) => void
    /** Callback fired whenever the view mode changes (edit vs create) */
    onViewModeChange?: (mode: ViewMode) => void
    /** Initial variant ID to select on mount (e.g. from URL) */
    initialSelectedVariantId?: number | null
}

export const ProductVariantsManager = forwardRef<ProductVariantsManagerHandle, ProductVariantsManagerProps>(
    function ProductVariantsManager({ product, onDirtyChange, onSavingChange, onSelectedVariantChange, onViewModeChange, initialSelectedVariantId }, ref): ReactElement {
    const { t } = useTranslation('features-products')
    const isMobile = useIsMobile()
    // Track user's explicit selection (null means no explicit selection yet)
    const [explicitSelectedVariantId, setExplicitSelectedVariantId] = useState<number | null>(initialSelectedVariantId ?? null)

    const [viewMode, setViewMode] = useState<ViewMode>('edit')
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)
    const queryClient = useQueryClient()

    // Filter out archived and discrete variants (discrete variants are managed in the product card)
    const activeVariants = product.variants.filter(v => !v.archived && !v.isDiscreteVariant)

    // Derive effective selected variant ID: use explicit selection, or default to first variant in edit mode
    // This avoids cascading renders from setState in useEffect
    const selectedVariantId = (() => {
        if (explicitSelectedVariantId !== null) {
            // Check if explicit selection still exists in active variants
            if (activeVariants.some(v => v.id === explicitSelectedVariantId)) {
                return explicitSelectedVariantId
            }
        }
        // Default to initialSelectedVariantId if valid, otherwise first variant
        if (viewMode === 'edit' && activeVariants.length > 0) {
            if (initialSelectedVariantId && activeVariants.some(v => v.id === initialSelectedVariantId)) {
                return initialSelectedVariantId
            }
            return activeVariants[0]?.id ?? null
        }
        return null
    })()

    // Track previous effective selection to notify parent only on changes
    const prevSelectedVariantIdRef = useRef<number | null>(selectedVariantId)
    useEffect(() => {
        if (prevSelectedVariantIdRef.current !== selectedVariantId) {
            prevSelectedVariantIdRef.current = selectedVariantId
            onSelectedVariantChange?.(selectedVariantId)
        }
    }, [selectedVariantId, onSelectedVariantChange])

    // Notify parent when viewMode changes
    useEffect(() => {
        onViewModeChange?.(viewMode)
    }, [viewMode, onViewModeChange])

    // Wrapper to set explicit selection and notify parent
    const setSelectedVariantId = (id: number | null): void => {
        setExplicitSelectedVariantId(id)
        onSelectedVariantChange?.(id)
    }

    const selectedVariant = activeVariants.find((v) => v.id === selectedVariantId)

    const form = useForm<VariantFormValues>({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- zod resolver type inference limitation
        resolver: zodResolver(variantFormSchema) as any,
        defaultValues: {
            name: '',
            sku: '',
            originalCost: '',
            originalCurrency: 'PLN',
            costDate: '',
            imageUrl: '',
        },
    })

    // Use useWatch for React Compiler compatibility (form.watch() causes memoization issues)
    const watchedImageUrl = useWatch({ control: form.control, name: 'imageUrl' })
    const watchedOriginalCost = useWatch({ control: form.control, name: 'originalCost' })
    const watchedOriginalCurrency = useWatch({ control: form.control, name: 'originalCurrency' })
    const watchedCostDate = useWatch({ control: form.control, name: 'costDate' })

    useEffect(() => {
        if (viewMode === 'create') {
            form.reset({
                name: '',
                sku: '',
                originalCost: '',
                originalCurrency: 'PLN',
                costDate: '',
                imageUrl: '',
            })
        } else if (selectedVariant) {
            form.reset({
                name: selectedVariant.name ?? '',
                sku: selectedVariant.sku ?? '',
                originalCost: formatCostFromNumber(selectedVariant.originalCost),
                originalCurrency: selectedVariant.originalCurrency ?? 'PLN',
                costDate: selectedVariant.costDate ? format(new Date(selectedVariant.costDate), 'yyyy-MM-dd') : '',
                imageUrl: selectedVariant.imageUrl ?? '',
            })
        }
    }, [selectedVariant, form, viewMode])

    // Track dirty state for edit mode only (create mode has its own flow)
    const isDirty = viewMode === 'edit' && form.formState.isDirty

    // Notify parent of dirty state changes
    useEffect(() => {
        onDirtyChange?.(isDirty)
    }, [isDirty, onDirtyChange])

    const createMutation = useMutation({
        mutationFn: (values: VariantFormValues) => {
            const payload: CreateVariantPayload = {
                name: values.name,
                sku: values.sku,
                originalCost: parseCostToNumber(values.originalCost),
                originalCurrency: values.originalCurrency || null,
                costDate: values.costDate || null,
                imageUrl: values.imageUrl || null,
            }
            return addProductVariants(product.id, [payload])
        },
        onSuccess: (createdVariants) => {
            toast.success(t('variants.toasts.createSuccess'))
            queryClient.invalidateQueries({ queryKey: ['product', product.id] })
            queryClient.invalidateQueries({ queryKey: ['products'] })
            queryClient.invalidateQueries({ queryKey: ['needs-review'] })
            // Switch to edit mode and select the new variant
            if (createdVariants.length > 0 && createdVariants[0]) {
                setViewMode('edit')
                setSelectedVariantId(createdVariants[0].id)
            }
        },
        onError: (err) => {
            toast.error(t('variants.toasts.createError'))
            console.error(err)
        },
    })

    const updateMutation = useMutation({
        mutationFn: (values: VariantFormValues) => {
            if (!selectedVariantId) throw new Error('No variant selected')
            return updateVariant(selectedVariantId, {
                ...values,
                originalCost: parseCostToNumber(values.originalCost),
                originalCurrency: values.originalCurrency || null,
                costDate: values.costDate || null,
            })
        },
        onSuccess: (data) => {
            // Build success message with auto-resolved info if present
            let message = t('variants.toasts.updateSuccess')
            if (data.autoResolved && data.autoResolved.length > 0) {
                const sources = [...new Set(data.autoResolved.map(e => e.source))]
                const count = data.autoResolved.length
                message = t(count === 1 ? 'variants.toasts.updateSuccessWithResolved' : 'variants.toasts.updateSuccessWithResolvedPlural', { count, sources: sources.join(', ') })
            }
            toast.success(message)
            queryClient.invalidateQueries({ queryKey: ['product', product.id] })
            queryClient.invalidateQueries({ queryKey: ['products'] })
            queryClient.invalidateQueries({ queryKey: ['needs-review'] })
        },
        onError: (err) => {
            toast.error(t('variants.toasts.updateError'))
            console.error(err)
        },
    })

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!selectedVariantId) throw new Error('No variant selected')
            return deleteVariant(selectedVariantId)
        },
        onSuccess: async () => {
            toast.success(t('variants.toasts.deleteSuccess'))
            setShowDeleteDialog(false)
            setSelectedVariantId(null)
            // Invalidate NeedsReview queries since deletion may create new unmatched listings
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['product', product.id] }),
                queryClient.invalidateQueries({ queryKey: ['products'] }),
                queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_QUERY_KEY }),
                queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_COUNT_QUERY_KEY }),
            ])
        },
        onError: (err) => {
            toast.error(t('variants.toasts.deleteError'))
            console.error(err)
        },
    })

    const isSaving = createMutation.isPending || updateMutation.isPending

    const onSubmit = (values: VariantFormValues): void => {
        if (viewMode === 'create') {
            createMutation.mutate(values)
        } else {
            updateMutation.mutate(values)
        }
    }

    useEffect(() => {
        onSavingChange?.(isSaving)
    }, [isSaving, onSavingChange])

    const handleCreateNew = (): void => {
        setViewMode('create')
        setSelectedVariantId(null)
    }

    const handleSelectVariant = (variantId: number): void => {
        setViewMode('edit')
        setSelectedVariantId(variantId)
    }

    // Expose handle to parent for coordinated saves
    useImperativeHandle(ref, () => ({
        isDirty,
        isSaving: updateMutation.isPending,
        submit: async () => {
            if (viewMode === 'edit' && isDirty) {
                await form.handleSubmit((values) => {
                    return new Promise<void>((resolve, reject) => {
                        updateMutation.mutate(values, {
                            onSuccess: () => resolve(),
                            onError: (err) => reject(err),
                        })
                    })
                })()
            }
        },
        reset: () => {
            if (selectedVariant) {
                form.reset({
                    name: selectedVariant.name ?? '',
                    sku: selectedVariant.sku ?? '',
                    originalCost: formatCostFromNumber(selectedVariant.originalCost),
                    originalCurrency: selectedVariant.originalCurrency ?? 'PLN',
                    costDate: selectedVariant.costDate ? format(new Date(selectedVariant.costDate), 'yyyy-MM-dd') : '',
                    imageUrl: selectedVariant.imageUrl ?? '',
                })
            }
        },
    }), [isDirty, viewMode, updateMutation, form, selectedVariant])


    // Compact collapsed state for discrete-only products (no explicit variants yet)
    const isCollapsedState = activeVariants.length === 0 && viewMode === 'edit'

    // Collapsed state: simple row with "Add variant" button
    if (isCollapsedState) {
        return (
            <section className="flex flex-col rounded-3xl border border-border/15 bg-card overflow-hidden">
                <button
                    onClick={handleCreateNew}
                    className="flex items-center gap-3 px-6 py-4 text-left hover:bg-muted/30 transition-colors group"
                >
                    <div className="h-8 w-8 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Plus className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                        <div className="text-sm font-medium">{t('variants.addVariant')}</div>
                        <div className="text-xs text-muted-foreground">{t('variants.addVariantDescription')}</div>
                    </div>
                </button>
            </section>
        )
    }

    return (
        <section className="flex flex-col rounded-3xl border border-border/15 bg-card overflow-hidden">
            <div className={cn(
                "flex h-full",
                isMobile && "flex-col"
            )}>
                {/* Variant List - Sidebar on desktop, stacked with horizontal scroll on mobile */}
                <div className={cn(
                    "border-border/15 flex flex-col bg-muted/10",
                    isMobile
                        ? "border-b"
                        : "w-1/3 border-r"
                )}>
                    <div className="p-3 sm:p-4 border-b border-border/15 flex items-center justify-between shrink-0">
                        <h3 className="text-sm font-medium text-muted-foreground">{t('variants.title')}</h3>
                        <div className="flex items-center gap-2 ml-2">
                            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                {activeVariants.length}
                            </span>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={handleCreateNew}
                                title={t('variants.addNew')}
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                    <div className={cn(
                        "p-2",
                        isMobile
                            ? "flex gap-2 overflow-x-auto"
                            : "flex-1 overflow-y-auto space-y-1"
                    )}>
                        {viewMode === 'create' && (
                            <div
                                className={cn(
                                    "text-left rounded-lg text-sm bg-secondary text-secondary-foreground font-medium flex items-center gap-2 shrink-0",
                                    isMobile
                                        ? "px-2 py-2 min-w-[120px] max-w-[140px]"
                                        : "px-3 py-3 w-full gap-3"
                                )}
                            >
                                <div className={cn(
                                    "rounded-md bg-primary/20 border border-primary/30 overflow-hidden flex-shrink-0 flex items-center justify-center",
                                    isMobile ? "h-9 w-9" : "h-8 w-8"
                                )}>
                                    <Plus className="h-4 w-4 text-primary" />
                                </div>
                                <div className={cn(
                                    "min-w-0",
                                    !isMobile && "flex-1"
                                )}>
                                    <div className={cn(
                                        "text-xs",
                                        isMobile ? "line-clamp-2 leading-tight" : "truncate"
                                    )}>{t('variants.newVariant')}</div>
                                    {!isMobile && <div className="text-xs text-muted-foreground truncate">{t('variants.creating')}</div>}
                                </div>
                            </div>
                        )}
                        {activeVariants.map((variant) => (
                            <button
                                key={variant.id}
                                onClick={() => handleSelectVariant(variant.id)}
                                className={cn(
                                    "text-left rounded-lg text-sm transition-colors flex items-center select-none shrink-0",
                                    isMobile
                                        ? "px-2 py-2 gap-2 min-w-[120px] max-w-[140px]"
                                        : "px-3 py-3 w-full gap-3",
                                    viewMode === 'edit' && selectedVariantId === variant.id
                                        ? "bg-secondary text-foreground font-medium"
                                        : "hover:bg-muted/50 text-foreground"
                                )}
                            >
                                <div className={cn(
                                    "rounded-md bg-muted border border-border/10 overflow-hidden flex-shrink-0",
                                    isMobile ? "h-9 w-9" : "h-8 w-8"
                                )}>
                                    {variant.imageUrl ? (
                                        <img src={variant.imageUrl} alt={variant.name} className="h-full w-full object-cover" />
                                    ) : (
                                        <div className="h-full w-full flex items-center justify-center bg-muted text-xs text-muted-foreground">
                                            {t('variants.imgPlaceholder')}
                                        </div>
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className={cn(
                                        "text-xs leading-tight",
                                        isMobile ? "line-clamp-2" : "truncate"
                                    )}>{variant.name || t('variants.unnamedVariant')}</div>
                                    {!isMobile && <div className="text-xs text-muted-foreground truncate">{variant.sku}</div>}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Right Content: Edit Form */}
                <div className="flex-1 flex flex-col overflow-y-auto">
                    {viewMode === 'create' ? (
                        <div className={cn(
                            "max-w-2xl mx-auto w-full",
                            isMobile ? "p-4" : "p-8"
                        )}>
                            <div className={cn(
                                "flex items-center",
                                isMobile ? "mb-4 gap-3" : "mb-8 gap-4"
                            )}>
                                <ImageUrlPopover
                                    trigger={
                                        <div className={cn(
                                            "rounded-xl border border-dashed border-border bg-muted/30 flex items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors overflow-hidden flex-shrink-0",
                                            isMobile ? "h-14 w-14" : "h-20 w-20"
                                        )}>
                                            {watchedImageUrl ? (
                                                <img
                                                    src={watchedImageUrl ?? ''}
                                                    alt="New variant"
                                                    className="object-cover w-full h-full"
                                                />
                                            ) : (
                                                <span className="text-muted-foreground text-xs text-center px-1">{t('variants.placeholders.addImage')}</span>
                                            )}
                                        </div>
                                    }
                                    currentImageUrl={watchedImageUrl}
                                    title={t('variants.imagePopover.title')}
                                    description={t('variants.imagePopover.description')}
                                    inputAriaLabel={t('variants.imagePopover.inputLabel')}
                                    onSave={async (url) => {
                                        form.setValue('imageUrl', url ?? '')
                                    }}
                                    successMessage={t('variants.toasts.imageUrlSet')}
                                    errorMessage={t('variants.toasts.imageUrlError')}
                                />

                                <div className="flex-1 min-w-0">
                                    <h2 className={cn(
                                        "font-bold tracking-tight",
                                        isMobile ? "text-lg" : "text-2xl"
                                    )}>{t('variants.createTitle')}</h2>
                                    <p className="text-sm text-muted-foreground mt-0.5 truncate">{t('variants.createSubtitle', { productName: product.name })}</p>
                                </div>
                            </div>

                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)} className={cn(
                                    isMobile ? "space-y-4" : "space-y-6"
                                )}>
                                    <div className={cn(
                                        "grid gap-4",
                                        isMobile ? "grid-cols-1" : "grid-cols-2 gap-6"
                                    )}>
                                        <FormField
                                            control={form.control}
                                            name="name"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t('variants.fields.name')}</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder={t('variants.placeholders.name')} {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="sku"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t('variants.fields.sku')}</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder={t('variants.placeholders.sku')} {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <CostInput
                                            costValue={watchedOriginalCost ?? ''}
                                            onCostChange={(val) => form.setValue('originalCost', val)}
                                            currencyValue={watchedOriginalCurrency}
                                            onCurrencyChange={(val) => form.setValue('originalCurrency', val)}
                                        />

                                        <div className="space-y-2">
                                            <Label>{t('variants.fields.costDate')}</Label>
                                            <DatePicker
                                                value={watchedCostDate ? new Date(watchedCostDate) : null}
                                                onChange={(date) => form.setValue('costDate', date ? format(date, 'yyyy-MM-dd') : '')}
                                                placeholder={t('variants.placeholders.selectDate')}
                                            />
                                        </div>
                                    </div>

                                    <div className={cn(
                                        "flex pt-4 gap-2",
                                        isMobile ? "flex-col-reverse" : "justify-end"
                                    )}>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className={isMobile ? "w-full" : ""}
                                            onClick={() => {
                                                setViewMode('edit')
                                                if (activeVariants.length > 0 && activeVariants[0]) {
                                                    setSelectedVariantId(activeVariants[0].id)
                                                }
                                            }}
                                        >
                                            {t('variants.actions.cancel')}
                                        </Button>
                                        <Button type="submit" disabled={createMutation.isPending} data-save-action="true" className={isMobile ? "w-full" : ""}>
                                            {createMutation.isPending && (
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            )}
                                            {!createMutation.isPending && <Plus className="mr-2 h-4 w-4" />}
                                            {t('variants.actions.create')}
                                        </Button>
                                    </div>
                                </form>
                            </Form>
                        </div>
                    ) : selectedVariant ? (
                        <div className={cn(
                            "max-w-2xl mx-auto w-full",
                            isMobile ? "p-4" : "p-8"
                        )}>
                            <div className={cn(
                                "flex items-start",
                                isMobile ? "mb-4 gap-3" : "mb-8 gap-4"
                            )}>
                                <ImageUrlPopover
                                    trigger={
                                        <div className={cn(
                                            "rounded-xl border border-border bg-muted/30 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity overflow-hidden flex-shrink-0",
                                            isMobile ? "h-14 w-14" : "h-20 w-20"
                                        )}>
                                            {selectedVariant.imageUrl ? (
                                                <img
                                                    src={selectedVariant.imageUrl}
                                                    alt={selectedVariant.name}
                                                    className="object-cover w-full h-full"
                                                />
                                            ) : (
                                                <span className="text-muted-foreground text-xs text-center px-1">{t('variants.clickToSetImage')}</span>
                                            )}
                                        </div>
                                    }
                                    currentImageUrl={selectedVariant.imageUrl}
                                    title={t('variants.imagePopover.title')}
                                    description={t('variants.imagePopover.description')}
                                    inputAriaLabel={t('variants.imagePopover.inputLabel')}
                                    onSave={async (url) => {
                                        await updateVariant(selectedVariant.id, { imageUrl: url || null })
                                        queryClient.invalidateQueries({ queryKey: ['product', product.id] })
                                    }}
                                    successMessage={t('variants.toasts.imageUpdateSuccess')}
                                    errorMessage={t('variants.toasts.imageUpdateError')}
                                />

                                <div className="flex-1 space-y-0.5 min-w-0">
                                    <FormField
                                        control={form.control}
                                        name="name"
                                        render={({ field }) => (
                                            <Editable.Root
                                                value={field.value}
                                                onValueChange={field.onChange}
                                                triggerMode="click"
                                                submitOnBlur={false}
                                            >
                                                <Editable.Area className="w-full flex items-center gap-1 group">
                                                    <Editable.Preview className={cn(
                                                        "font-bold tracking-tight px-2 py-1 border-none rounded-md focus-visible:ring-0 cursor-text truncate",
                                                        isMobile ? "text-lg" : "text-2xl"
                                                    )} />
                                                    <Editable.Trigger className="text-muted-foreground/30 hover:text-foreground transition-colors focus:outline-none shrink-0">
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </Editable.Trigger>
                                                    <Editable.Input className={cn(
                                                        "font-bold tracking-tight px-2 py-1 h-auto w-full",
                                                        isMobile ? "text-lg" : "text-2xl"
                                                    )} />
                                                </Editable.Area>
                                            </Editable.Root>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="sku"
                                        render={({ field }) => (
                                            <Editable.Root
                                                value={field.value}
                                                onValueChange={field.onChange}
                                                triggerMode="click"
                                                submitOnBlur={false}
                                            >
                                                <Editable.Area className="w-full flex items-center gap-1 group">
                                                    <Editable.Preview className="text-sm text-muted-foreground px-2 py-1 border-none rounded-md focus-visible:ring-0 cursor-text truncate" />
                                                    <Editable.Trigger className="text-muted-foreground/30 hover:text-foreground transition-colors focus:outline-none shrink-0">
                                                        <Pencil className="h-3 w-3" />
                                                    </Editable.Trigger>
                                                    <Editable.Input className="text-sm text-muted-foreground px-2 py-1 h-auto w-full" />
                                                </Editable.Area>
                                            </Editable.Root>
                                        )}
                                    />
                                </div>

                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="size-8 flex-shrink-0">
                                            <EllipsisVertical className="size-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                            variant="destructive"
                                            onClick={() => setShowDeleteDialog(true)}
                                        >
                                            <Trash2 className="size-4" />
                                            {t('variants.actions.delete')}
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>

                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)} className={cn(
                                    isMobile ? "space-y-4" : "space-y-6"
                                )}>
                                    <div className={cn(
                                        "grid gap-4",
                                        isMobile ? "grid-cols-1" : "grid-cols-2 gap-6"
                                    )}>

                                        <CostInput
                                            costValue={watchedOriginalCost ?? ''}
                                            onCostChange={(val) => form.setValue('originalCost', val, { shouldDirty: true })}
                                            currencyValue={watchedOriginalCurrency}
                                            onCurrencyChange={(val) => form.setValue('originalCurrency', val, { shouldDirty: true })}
                                        />

                                        <div className="space-y-2">
                                            <Label>{t('variants.fields.costDate')}</Label>
                                            <DatePicker
                                                value={watchedCostDate ? new Date(watchedCostDate) : null}
                                                onChange={(date) => form.setValue('costDate', date ? format(date, 'yyyy-MM-dd') : '', { shouldDirty: true })}
                                                placeholder={t('variants.placeholders.selectDate')}
                                            />
                                        </div>
                                    </div>
                                </form>
                            </Form>
                        </div>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-muted-foreground">
                            {t('variants.selectToEdit')}
                        </div>
                    )}
                </div>
            </div>

            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{t('variants.deleteDialog.title')}</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground whitespace-pre-line">
                        {t('variants.deleteDialog.description')}
                    </p>
                    <DialogFooter className="flex flex-row justify-end gap-2">
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={() => deleteMutation.mutate()}
                            disabled={deleteMutation.isPending}
                        >
                            {deleteMutation.isPending ? t('variants.deleteDialog.deleting') : t('variants.deleteDialog.confirm')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section >
    )
})
